import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { BrowserRouter, useLocation, useNavigate } from "react-router-dom";
import { CRTNavigationContext } from "./CRTNavigationContext";

interface CRTRouterProps {
  children: ReactNode;
  /** Duration of page transitions in milliseconds */
  transitionDuration?: number;
}

interface NavigationState {
  isTransitioning: boolean;
  transitionDirection: "forward" | "back";
}

function CRTRouterInner({
  children,
  transitionDuration = 300,
}: CRTRouterProps) {
  const navigate = useNavigate();
  const location = useLocation();

  // Use state for history to trigger re-renders
  const [historyStack, setHistoryStack] = useState<string[]>([
    location.pathname,
  ]);
  const isNavigatingBackRef = useRef(false);

  const [navState, setNavState] = useState<NavigationState>({
    isTransitioning: false,
    transitionDirection: "forward",
  });

  // Track navigation history
  useEffect(() => {
    const currentPath = location.pathname;

    if (isNavigatingBackRef.current) {
      // We're navigating back, remove the last item
      setHistoryStack((prev) => prev.slice(0, -1));
      isNavigatingBackRef.current = false;
    } else {
      // Forward navigation, add to stack
      setHistoryStack((prev) => {
        if (prev[prev.length - 1] !== currentPath) {
          return [...prev, currentPath];
        }
        return prev;
      });
    }
  }, [location.pathname]);

  const handleNavigate = useCallback(
    (path: string) => {
      if (path !== location.pathname && !navState.isTransitioning) {
        setNavState((prev) => ({
          ...prev,
          isTransitioning: true,
          transitionDirection: "forward",
        }));
        navigate(path);
        // End transition after duration
        setTimeout(() => {
          setNavState((prev) => ({
            ...prev,
            isTransitioning: false,
          }));
        }, transitionDuration);
      }
    },
    [navigate, location.pathname, navState.isTransitioning, transitionDuration],
  );

  const handleGoBack = useCallback(() => {
    if (historyStack.length > 1 && !navState.isTransitioning) {
      isNavigatingBackRef.current = true;
      setNavState((prev) => ({
        ...prev,
        isTransitioning: true,
        transitionDirection: "back",
      }));
      navigate(-1);
      // End transition after duration
      setTimeout(() => {
        setNavState((prev) => ({
          ...prev,
          isTransitioning: false,
        }));
      }, transitionDuration);
    } else {
      navigate("/");
    }
  }, [
    navigate,
    historyStack.length,
    navState.isTransitioning,
    transitionDuration,
  ]);

  const canGoBack = location.pathname !== "/";

  // Handle Escape key for back navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && canGoBack && !navState.isTransitioning) {
        e.preventDefault();
        handleGoBack();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [canGoBack, handleGoBack, navState.isTransitioning]);

  const contextValue = useMemo(
    () => ({
      navigate: handleNavigate,
      goBack: handleGoBack,
      canGoBack,
      currentPath: location.pathname,
      isTransitioning: navState.isTransitioning,
      transitionDirection: navState.transitionDirection,
    }),
    [
      handleNavigate,
      handleGoBack,
      canGoBack,
      location.pathname,
      navState.isTransitioning,
      navState.transitionDirection,
    ],
  );

  return (
    <CRTNavigationContext.Provider value={contextValue}>
      {children}
    </CRTNavigationContext.Provider>
  );
}

export function CRTRouter({ children, transitionDuration }: CRTRouterProps) {
  return (
    <BrowserRouter>
      <CRTRouterInner transitionDuration={transitionDuration}>
        {children}
      </CRTRouterInner>
    </BrowserRouter>
  );
}
